import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import ArrowIcon from '../components/ArrowIcon';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver, ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';
import { findService } from '../data/sectorServicesData';

/* Splits a service title into a plain lead and an italic accent — the accent
   is the trailing parenthetical if there is one ("Remote Patient Monitoring
   (RPM)"), otherwise just the last word ("... Solutions"). */
function splitTitle(title) {
  const parenIdx = title.indexOf(' (');
  if (parenIdx !== -1) return [title.slice(0, parenIdx), title.slice(parenIdx + 1)];
  const lastSpace = title.lastIndexOf(' ');
  if (lastSpace === -1) return [title, ''];
  return [title.slice(0, lastSpace), title.slice(lastSpace + 1)];
}

function splitSentences(text) {
  return text
    .split('. ')
    .map((s, i, arr) => (i < arr.length - 1 ? `${s.trim()}.` : s.trim()))
    .filter(Boolean);
}

/* Turns the service's one-paragraph "Our Process" description into the same
   numbered, illustrated step format ProcessSection expects — each sentence
   becomes a step, and the "What We Deliver" bullets (straight from the
   source brief, nothing invented) are distributed across the steps as the
   supporting points under each one. */
const STEP_TITLES = {
  2: ['Assess & Design', 'Deploy & Handover'],
  3: ['Assess & Plan', 'Design & Deploy', 'Train & Handover'],
};
function buildProcessSteps(service) {
  const sentences = splitSentences(service.process);
  const titles = STEP_TITLES[sentences.length] || sentences.map((_, i) => `Phase ${i + 1}`);
  return sentences.map((desc, i) => ({
    num: String(i + 1).padStart(2, '0'),
    title: titles[i] || `Phase ${i + 1}`,
    desc,
    points: service.deliver.filter((_, di) => di % sentences.length === i),
  }));
}

/* Honest, non-specific deployment scenarios — no invented client names or
   performance metrics. The "metric" field is a short descriptive phrase
   rather than a fabricated statistic, since we have no real project data
   to report per service yet. */
function buildCaseStudies(service, sector) {
  return [
    {
      tag: 'Pilot Deployment',
      metric: 'Start Small',
      title: `A Single-Site ${service.title} Pilot`,
      desc: `Most engagements begin with one site — validating ${service.title.toLowerCase()} against your real operating conditions, workflows, and existing systems before any wider commitment.`,
    },
    {
      tag: 'Multi-Site Rollout',
      metric: 'Built to Scale',
      title: `Rolling Out Across Multiple ${sector.name} Sites`,
      desc: `Once a pilot is validated, the same architecture extends across additional sites — centralised dashboards, consistent alerting, and one platform instead of a patchwork of point solutions.`,
    },
    {
      tag: 'Retrofit Integration',
      metric: 'No Rip-and-Replace',
      title: 'Integrating with What You Already Run',
      desc: `${service.title} is designed to connect to your existing infrastructure rather than replace it — we work around your current hardware, software, and processes wherever possible.`,
    },
  ];
}

/* Generic, honest FAQ content — logistics and integration questions only,
   no invented performance metrics or client results. */
function buildFaqs(service, sector) {
  return [
    {
      q: `How long does it take to deploy ${service.title}?`,
      a: `Timelines depend on scope and your existing infrastructure, but most ${sector.name.toLowerCase()} deployments move from initial assessment to a working pilot within a few weeks — full rollout follows once the pilot is validated on-site.`,
    },
    {
      q: 'Will this integrate with our existing systems?',
      a: "Yes. We design around your current infrastructure rather than asking you to replace it — connecting to existing hardware, software, and data systems wherever possible, and flagging any gaps upfront during the assessment phase.",
    },
    {
      q: 'Is ongoing support included after deployment?',
      a: 'Yes. Zone-X stays engaged after go-live — monitoring performance, handling maintenance alerts, and supporting your team as the system scales or your requirements evolve.',
    },
    {
      q: `Do you work with ${sector.name.toLowerCase()} organisations of any size?`,
      a: `Yes. We work with single-site pilots as well as multi-site rollouts across ${sector.name.toLowerCase()}. Our process is designed to scale from a first deployment to a full network without a re-architecture.`,
    },
  ];
}

function IntroCard({ service, sector }) {
  const [ref, visible] = useReveal();
  const color = sector.color;
  return (
    <section style={{ padding: '0 0 clamp(32px,4vw,52px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>
        <div
          ref={ref}
          className="iot-intro-grid"
          style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(24px,4vw,56px)',
            background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(10,10,20,0.08)',
            borderRadius: 24, padding: 'clamp(28px,4vw,52px)',
            opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(22px)',
            transition: 'opacity 520ms, transform 520ms',
          }}
        >
          <div>
            <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 10.5, letterSpacing: '0.06em', color, textTransform: 'uppercase' }}>ABOUT THIS SERVICE</span>
            <h2 style={{ fontFamily: "'Anton',sans-serif", fontSize: 'clamp(22px,3vw,36px)', lineHeight: 1.1, marginTop: 8, marginBottom: 0, color: 'var(--ink)' }}>
              Engineered for{' '}
              <span style={{ fontFamily: "'Instrument Serif',serif", fontStyle: 'italic', fontWeight: 400, color }}>{sector.name}</span>
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 15, lineHeight: 1.82, color: 'var(--ink-soft)' }}>{sector.deliveryNote}</p>
            <p style={{ fontSize: 15, lineHeight: 1.82, color: 'var(--ink-soft)' }}>Here's exactly how we take {service.title.toLowerCase()} from first conversation to a live deployment.</p>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.iot-intro-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}

function MoreFromSector({ sector, currentSlug }) {
  const [ref, visible] = useReveal();
  const siblings = sector.services.filter(s => s.slug !== currentSlug);
  if (siblings.length === 0) return null;
  return (
    <section style={{ padding: '0 0 clamp(48px,6vw,80px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)' }}>
        <div
          ref={ref}
          style={{
            border: '1px solid rgba(10,10,20,0.08)', borderRadius: 24, padding: 'clamp(24px,3vw,36px)',
            opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity 480ms, transform 480ms',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: sector.color, textTransform: 'uppercase' }}>
              More in {sector.name}
            </span>
            <Link to={sector.to} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: sector.color }}>
              View All {sector.name} Solutions <ArrowIcon size={11} />
            </Link>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9 }}>
            {siblings.map(s => (
              <Link
                key={s.slug}
                to={`${sector.to}/${s.slug}`}
                className="hoverable"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '9px 15px',
                  background: `${sector.color}0A`,
                  border: `1px solid ${sector.color}26`,
                  borderRadius: 999,
                  fontSize: 13, fontWeight: 600, color: 'var(--ink)',
                }}
              >
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: sector.color, flexShrink: 0 }} />
                {s.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ServiceDetailPage() {
  const { sectorSlug, serviceSlug } = useParams();
  const found = findService(sectorSlug, serviceSlug);
  const [heroRef, heroVisible] = useReveal();

  if (!found) return <Navigate to="/sectors" replace />;
  const { sector, service } = found;
  const [titleMain, titleAccent] = splitTitle(service.title);
  const steps = buildProcessSteps(service);
  const caseStudies = buildCaseStudies(service, sector);
  const faqs = buildFaqs(service, sector);
  const deliverItems = service.deliver.map(d => ({ title: d, desc: '' }));

  return (
    <>
      <Helmet>
        <title>{service.title} — {sector.name} IoT Solutions | Zone-X</title>
        <meta name="description" content={service.brief} />
        <link rel="canonical" href={`https://zone-x.dev${sector.to}/${service.slug}`} />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag={`${sector.name} / Service`}
          breadcrumb={service.title}
          title={titleMain}
          titleAccent={titleAccent}
          subtitle={service.brief}
          geo={`${sector.name} IoT solutions delivered by Zone-X across India.`}
          color={sector.color}
          visible={heroVisible}
          parentLabel={sector.name}
          parentHref={sector.to}
          visual={<img src={service.img || sector.heroImg} alt={service.title} style={{ width: '100%', display: 'block' }} />}
        />
      </div>

      <StatsRow />
      <IntroCard service={service} sector={sector} />
      <ProcessSection steps={steps} color={sector.color} />
      <WhatWeDeliver items={deliverItems} color={sector.color} />
      <ServiceCaseStudies studies={caseStudies} color={sector.color} />
      <ServiceFAQ faqs={faqs} color={sector.color} />
      <IndustriesSection />
      <WhyZoneX />
      <MoreFromSector sector={sector} currentSlug={service.slug} />
      <ServiceCTA />
      <Footer />
    </>
  );
}
